<template>
    <div v-if="visible || urgentDialog">
        <!-- ⚠ sticky 與 top 由外層的 `BannerStack` 負責，這裡刻意**不要**再設一次：
             兩個 sticky 兄弟給同樣的 top 會疊在一起。 -->
        <v-alert v-if="visible" dense tile class="mb-0"
            :color="isUrgent ? 'red darken-2' : 'orange darken-1'" dark dismissible
            @input="onDismiss">
            <v-icon small class="mr-2">{{ isUrgent ? 'mdi-alert-octagon' : 'mdi-alert' }}</v-icon>
            <span class="text-body-2">
                {{ bannerText }}
            </span>
            <v-btn small text outlined class="ml-3 normal-case" @click="goToAlerts">
                {{ $t('safety_alert.banner.view_now') }}
            </v-btn>
        </v-alert>

        <v-dialog v-model="urgentDialog" width="420" persistent>
            <v-card>
                <v-card-title class="red--text text--darken-2">
                    <v-icon color="red darken-2" class="mr-2">mdi-alert-octagon</v-icon>
                    {{ $t('safety_alert.urgent_dialog.title') }}
                </v-card-title>
                <v-card-text>
                    {{ $t('safety_alert.urgent_dialog.description') }}
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text class="normal-case" @click="closeUrgentDialog">
                        {{ $t('safety_alert.urgent_dialog.later') }}
                    </v-btn>
                    <v-btn color="red darken-2" dark class="normal-case" @click="goToAlertsFromDialog">
                        {{ $t('safety_alert.urgent_dialog.view_now') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { safetyAlertServices } from '@/services/safetyAlert.js';

const DISMISS_KEY = 'sa_banner_dismissed';
const URGENT_PROMPTED_KEY = 'sa_urgent_prompted';

/**
 * 危險字眼警示全站提示(分級):
 * - 有未處理、max_severity < 3 → 橘色警示條
 * - max_severity = 3 → 紅色警示條 + 該 session 首次進入時彈出對話框
 * 關閉紀錄存 sessionStorage,unhandled_count/max_severity 變動時重新顯示。
 * 見 docs/plans/SafetyAlert.web.impl-plan.md
 */
export default {
    name: 'SafetyAlertBanner',
    data() {
        return {
            unhandledCount: 0,
            maxSeverity: 0,
            dismissed: false,
            urgentDialog: false,
        };
    },
    computed: {
        /** 在日記本內(/diary/homework/:id/*)時只看該日記本的警示 */
        homeworkId() {
            if (this.$route.params.id != null &&
                this.$route.path.indexOf('/diary/homework/') === 0) {
                return parseInt(this.$route.params.id);
            }
            return null;
        },
        isUrgent() {
            return this.maxSeverity >= 3;
        },
        visible() {
            return this.unhandledCount > 0 && !this.dismissed;
        },
        summaryKey() {
            return `${this.homeworkId ?? 'all'}:${this.unhandledCount}:${this.maxSeverity}`;
        },
        bannerText() {
            const base = this.$t('safety_alert.banner.text').replace('${num}', this.unhandledCount);
            if (this.isUrgent) {
                return base + this.$t('safety_alert.banner.urgent_suffix');
            }
            return base;
        },
    },
    async created() {
        await this.refresh();
        // 列表頁處理完警示後通知重抓,讓警示條即時消失/更新
        this.$root.$on('safety-summary-refresh', this.refresh);
    },
    beforeDestroy() {
        this.$root.$off('safety-summary-refresh', this.refresh);
    },
    watch: {
        // 同 layout 下切換日記本時重抓(scope 會變)
        homeworkId() {
            this.refresh();
        },
    },
    methods: {
        async refresh() {
            try {
                const res = await safetyAlertServices.getSummary(this.homeworkId);
                this.unhandledCount = res.data?.unhandled_count ?? 0;
                this.maxSeverity = res.data?.max_severity ?? 0;
            } catch (err) {
                // 摘要失敗不影響頁面主功能
                this.unhandledCount = 0;
                return;
            }
            this.dismissed = sessionStorage.getItem(DISMISS_KEY) === this.summaryKey;
            const onAlertPage = ['safetyAlerts', 'homeworkSafetyAlerts'].includes(this.$route.name);
            if (this.isUrgent && this.unhandledCount > 0 &&
                sessionStorage.getItem(URGENT_PROMPTED_KEY) !== this.summaryKey &&
                !onAlertPage) {
                this.urgentDialog = true;
            }
        },
        onDismiss() {
            sessionStorage.setItem(DISMISS_KEY, this.summaryKey);
            this.dismissed = true;
        },
        markUrgentPrompted() {
            sessionStorage.setItem(URGENT_PROMPTED_KEY, this.summaryKey);
        },
        closeUrgentDialog() {
            this.markUrgentPrompted();
            this.urgentDialog = false;
        },
        goToAlerts() {
            if (this.homeworkId != null) {
                if (this.$route.name !== 'homeworkSafetyAlerts') {
                    this.$router.push({
                        name: 'homeworkSafetyAlerts',
                        params: { id: String(this.homeworkId) },
                    });
                }
                return;
            }
            if (this.$route.name !== 'safetyAlerts') {
                this.$router.push({ name: 'safetyAlerts' });
            }
        },
        goToAlertsFromDialog() {
            this.markUrgentPrompted();
            this.urgentDialog = false;
            this.goToAlerts();
        },
    },
};
</script>
